<script lang="ts">
  import { formatIST } from '$lib/utils/dates';
  // "Emails" tab: the onboarding door (shared EmailAccountConnect form) when
  // nothing is connected yet, then a per-account status strip + the
  // unmatched-sender review queue (EmailReviewQueue) once at least one
  // mailbox is connected. Admins get a third pill, "All inboxes", for
  // managing every user's mailboxes/review queues (AdminAllInboxesPanel). See
  // internal/api/handlers/email_review.go / email_account.go for the backend
  // this drives.
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Mail, CheckCircle2, XCircle, LoaderCircle, ChevronDown, ChevronRight, ArrowDownLeft, ArrowUpRight, ArrowUpDown, Lock, Globe } from 'lucide-svelte';
  import { meEmailAccountApi, meEmailReviewApi, meEmailBackfillApi, meTrackedDomainsApi } from '$lib/api/me';
  import { peopleApi, companiesApi, activitiesApi } from '$lib/api/crm';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import EmailAccountConnect from '$lib/components/EmailAccountConnect.svelte';
  import EmailReviewQueue from '$lib/components/entities/EmailReviewQueue.svelte';
  import AdminAllInboxesPanel from '$lib/components/admin/AdminAllInboxesPanel.svelte';
  import EmailActivityModal from '$lib/components/entities/EmailActivityModal.svelte';
  import SearchBar from '$lib/components/ui/SearchBar.svelte';
  import Pagination from '$lib/components/ui/Pagination.svelte';
  import { groupEmailThreads, displayRowKey, newestMessage, sortMessagesDesc, threadParticipants } from '$lib/utils/emailThreads';
  import type { Activity } from '$lib/types';

  const queryClient = useQueryClient();

  function isAccessRevokedError(err: unknown): boolean {
    return getErrorMessage(err).toLowerCase().includes('not enabled for your account');
  }

  // Three internal tabs, same idiom as ActivitiesPage's All/Upcoming.
  // Default to Team inbox for anyone who can actually see it (admins bypass
  // the email_access gate entirely; a member needs the flag) — a connected
  // personal mailbox implies email_access already, so it needs no separate
  // check here. Computed once at mount, not kept in sync with later access
  // changes — a manual tab pick should stick for the session.
  let innerTab = $state<'team-inbox' | 'review-queue' | 'all-inboxes'>(
    session.isAdmin || !!session.me?.user.email_access ? 'team-inbox' : 'review-queue'
  );

  // Every mailbox the caller has connected — a user may connect more than
  // one (see migration 00023), so the status card below renders one row per
  // account rather than only the first.
  const accountsQuery = createQuery(() => ({
    queryKey: ['me-email-accounts'],
    queryFn: () => meEmailAccountApi.list()
  }));
  const accounts = $derived(accountsQuery.data?.results ?? []);
  const connected = $derived(accounts.length > 0);
  const emailAccessDisabled = $derived(accountsQuery.isError && isAccessRevokedError(accountsQuery.error));

  // ===================== Team inbox =====================
  // Reuses the existing activities endpoint (type=email) rather than a new
  // one — the admin's vertical switcher, thread grouping, and
  // EmailActivityModal are all shared with ActivitiesPage.
  let inboxSearch = $state('');
  let inboxPage = $state(1);
  let inboxOrder = $state<'asc' | 'desc'>('desc');

  const inboxVerticalId = $derived(session.selectedVertical === 'all' ? undefined : session.selectedVertical);

  const inboxQuery = createQuery(() => ({
    queryKey: ['team-inbox', { inboxSearch, inboxPage, inboxVerticalId, inboxOrder }],
    queryFn: () =>
      activitiesApi.list({
        q: inboxSearch || undefined,
        page: inboxPage,
        per_page: 25,
        type: 'email',
        vertical_id: inboxVerticalId,
        order: inboxOrder
      }),
    enabled: innerTab === 'team-inbox'
  }));

  // Cheap name/direction lookups for the row chips — same
  // queryKey/per_page:100 idiom as ActivitiesPage, so the cache is shared
  // when both pages have been visited.
  const inboxCompaniesQuery = createQuery(() => ({
    queryKey: ['companies-picker', { verticalId: inboxVerticalId }],
    queryFn: () => companiesApi.list({ per_page: 100, vertical_id: inboxVerticalId }),
    enabled: innerTab === 'team-inbox'
  }));
  const inboxPeopleQuery = createQuery(() => ({
    queryKey: ['people-picker', { verticalId: inboxVerticalId }],
    queryFn: () => peopleApi.list({ per_page: 100, vertical_id: inboxVerticalId }),
    enabled: innerTab === 'team-inbox'
  }));

  $effect(() => {
    inboxSearch;
    inboxVerticalId;
    inboxOrder;
    inboxPage = 1;
  });

  const inboxDisplayRows = $derived(groupEmailThreads(inboxQuery.data?.results ?? []));
  let inboxExpandedThreads = $state<Set<string>>(new Set());
  function toggleInboxThread(key: string) {
    const next = new Set(inboxExpandedThreads);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    inboxExpandedThreads = next;
  }

  function inboxDomainOf(addr: string): string {
    return addr.split('@')[1]?.toLowerCase().trim() ?? '';
  }
  // Same cheap heuristic as ActivitiesPage.emailDirection: no mailbox/folder
  // round trip, just a From-address match against the linked person/company.
  function inboxDirection(row: Activity): 'received' | 'sent' {
    const from = row.email?.from_addr?.toLowerCase() ?? '';
    const person = row.person_id
      ? inboxPeopleQuery.data?.results.find((p) => p.id === row.person_id)
      : undefined;
    if (person?.email && person.email.toLowerCase() === from) return 'received';
    const company = row.company_id
      ? inboxCompaniesQuery.data?.results.find((c) => c.id === row.company_id)
      : undefined;
    if (company?.email && inboxDomainOf(company.email) === inboxDomainOf(from)) return 'received';
    return 'sent';
  }
  function inboxPersonName(id: number): string {
    const p = inboxPeopleQuery.data?.results.find((x) => x.id === id);
    if (!p) return '';
    return `${p.first_name} ${p.last_name}`.trim() || p.email || '';
  }
  function inboxCompanyName(id: number): string {
    return inboxCompaniesQuery.data?.results.find((c) => c.id === id)?.name ?? '';
  }
  // Person has no dedicated detail route — mirrors Header.svelte's topbar
  // search "pick" pattern (requestEdit + navigate to the People tab). A
  // company does have one (CompanyDetail via ?id=), so that's a direct push.
  function openPersonChip(id: number) {
    const p = inboxPeopleQuery.data?.results.find((x) => x.id === id);
    if (!p) return;
    session.requestEdit('people', p);
    route.push({ tab: 'people' });
  }
  function openCompanyChip(id: number) {
    route.push({ tab: 'companies', id });
  }

  let inboxDetailRow = $state<Activity | null>(null);

  // ===================== Domains tracked in your vertical (read-only) =====================
  let trackedDomainsOpen = $state(false);
  const trackedDomainsQuery = createQuery(() => ({
    queryKey: ['me-tracked-domains'],
    queryFn: () => meTrackedDomainsApi.list(),
    enabled: connected
  }));

  // ===================== status strip: sync now (per account) =====================
  // Feedback is a toast (not an inline note) so it's visible even after the
  // user has scrolled down the review queue — see build brief item 5.
  const syncMutation = createMutation(() => ({
    mutationFn: (id: number) => meEmailAccountApi.syncNow(id),
    onSuccess: (result) => {
      toast.info(result.ran ? 'Sync complete.' : (result.detail ?? 'A sync is already in progress.'));
      queryClient.invalidateQueries({ queryKey: ['me-email-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['email-review'] });
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));
</script>

<div class="tym-page max-w-[880px]">
  {#if accountsQuery.isLoading}
    <p class="tym-loading-state">Loading&hellip;</p>
  {:else if emailAccessDisabled}
    <p class="tym-error-state">Email access is disabled for your account — ask an admin.</p>
  {:else}
    <div class="mb-4 flex flex-wrap items-center gap-2">
      <button
        type="button"
        class="tym-chip {innerTab === 'team-inbox' ? 'active' : ''}"
        onclick={() => (innerTab = 'team-inbox')}
      >
        Team inbox
      </button>
      <button
        type="button"
        class="tym-chip {innerTab === 'review-queue' ? 'active' : ''}"
        onclick={() => (innerTab = 'review-queue')}
      >
        My review queue
      </button>
      {#if session.isAdmin}
        <button
          type="button"
          class="tym-chip {innerTab === 'all-inboxes' ? 'active' : ''}"
          onclick={() => (innerTab = 'all-inboxes')}
        >
          All inboxes
        </button>
      {/if}
    </div>

    {#if innerTab === 'team-inbox'}
      <div class="flex flex-wrap items-start justify-between gap-3">
        <SearchBar bind:value={inboxSearch} />
        <button
          type="button"
          class="btn-secondary"
          onclick={() => (inboxOrder = inboxOrder === 'desc' ? 'asc' : 'desc')}
          title="Toggle sort order"
        >
          <ArrowUpDown class="h-4 w-4" />
          {inboxOrder === 'desc' ? 'Newest first' : 'Oldest first'}
        </button>
      </div>

      {#if inboxQuery.isLoading}
        <p class="tym-loading-state">Loading&hellip;</p>
      {:else if inboxQuery.isError}
        <p class="tym-error-state">{getErrorMessage(inboxQuery.error)}</p>
      {:else if inboxDisplayRows.length === 0}
        <p class="tym-empty-state">No team emails yet — they appear as your team&apos;s mailboxes sync.</p>
      {:else}
        {#snippet inboxRow(row: Activity)}
          <div
            class="tym-card cursor-pointer px-4 py-3.5"
            role="button"
            tabindex="0"
            onclick={() => (inboxDetailRow = row)}
            onkeydown={(e) => e.key === 'Enter' && (inboxDetailRow = row)}
          >
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  {#if inboxDirection(row) === 'received'}
                    <ArrowDownLeft class="h-3.5 w-3.5 shrink-0 text-dark-gray" />
                  {:else}
                    <ArrowUpRight class="h-3.5 w-3.5 shrink-0 text-dark-gray" />
                  {/if}
                  <span class="truncate text-[13.5px] font-semibold text-navy">{row.email?.from_addr || '—'}</span>
                  {#if row.email?.visibility === 'admin'}
                    <span
                      class="tym-badge inline-flex shrink-0 items-center gap-1"
                      style="background:#FBF0DC; color:#8a5c00"
                      title="Only visible to admins"
                    >
                      <Lock class="h-3 w-3" /> Management only
                    </span>
                  {/if}
                </div>
                <div class="mt-0.5 truncate text-[13px] text-navy">{row.email?.subject || row.title || '(no subject)'}</div>
                {#if row.email?.snippet}
                  <p class="mt-0.5 line-clamp-1 text-[12px] text-dark-gray">{row.email.snippet}</p>
                {/if}
                {#if row.person_id || row.company_id}
                  <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {#if row.person_id}
                      <button
                        type="button"
                        class="tym-chip !min-h-0 !px-2 !py-0.5 !text-[11px]"
                        onclick={(e) => {
                          e.stopPropagation();
                          openPersonChip(row.person_id as number);
                        }}
                      >
                        {inboxPersonName(row.person_id)}
                      </button>
                    {/if}
                    {#if row.company_id}
                      <button
                        type="button"
                        class="tym-chip !min-h-0 !px-2 !py-0.5 !text-[11px]"
                        onclick={(e) => {
                          e.stopPropagation();
                          openCompanyChip(row.company_id as number);
                        }}
                      >
                        {inboxCompanyName(row.company_id)}
                      </button>
                    {/if}
                  </div>
                {/if}
              </div>
              <span class="shrink-0 text-[11.5px] text-dark-gray">
                {(row.email?.sent_at ?? row.created_at).slice(0, 16).replace('T', ' ')}
              </span>
            </div>
          </div>
        {/snippet}
        <div class="space-y-2.5">
          {#each inboxDisplayRows as entry (displayRowKey(entry))}
            {#if entry.kind === 'thread' && entry.messages.length > 1}
              {@const newest = newestMessage(entry.messages)}
              {@const open = inboxExpandedThreads.has(entry.key)}
              <div
                class="tym-card cursor-pointer px-4 py-3.5"
                role="button"
                tabindex="0"
                onclick={() => toggleInboxThread(entry.key)}
                onkeydown={(e) => e.key === 'Enter' && toggleInboxThread(entry.key)}
              >
                <div class="flex items-center gap-3">
                  <ChevronRight class="h-4 w-4 shrink-0 text-dark-gray transition-transform {open ? 'rotate-90' : ''}" />
                  <Mail class="h-4 w-4 shrink-0 text-toyama-teal" />
                  <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="truncate text-[13.5px] font-semibold text-navy"
                        >{newest.email?.subject || newest.title || '(no subject)'}</span
                      >
                      <span class="tym-badge shrink-0" style="background:#EAE6F3; color:#5a4b8a"
                        >{entry.messages.length} messages</span
                      >
                    </div>
                    <div class="mt-0.5 truncate text-[11.5px] text-dark-gray">{threadParticipants(entry.messages)}</div>
                  </div>
                  <span class="shrink-0 text-[11.5px] text-dark-gray">
                    {(newest.email?.sent_at ?? newest.created_at).slice(0, 16).replace('T', ' ')}
                  </span>
                </div>
              </div>
              {#if open}
                <div class="ml-6 space-y-2">
                  {#each sortMessagesDesc(entry.messages) as row (row.id)}
                    {@render inboxRow(row)}
                  {/each}
                </div>
              {/if}
            {:else}
              {@render inboxRow(entry.kind === 'thread' ? entry.messages[0] : entry.activity)}
            {/if}
          {/each}
        </div>
        <Pagination
          page={inboxQuery.data?.page ?? 1}
          perPage={inboxQuery.data?.per_page ?? 25}
          count={inboxQuery.data?.count ?? 0}
          onChange={(p) => (inboxPage = p)}
        />
      {/if}
    {:else if innerTab === 'all-inboxes'}
      <AdminAllInboxesPanel />
    {:else if !connected}
      <EmailAccountConnect />
    {:else}
    <div class="mb-5 space-y-2.5">
      {#each accounts as acct (acct.id)}
        <div class="tym-card flex flex-wrap items-center gap-3 px-4 py-3.5">
          <Mail class="h-5 w-5 shrink-0 text-toyama-teal" />
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              {#if acct.status === 'connected'}
                <CheckCircle2 class="h-4 w-4 shrink-0 text-toyama-lime" />
              {:else if acct.status === 'error'}
                <XCircle class="h-4 w-4 shrink-0 text-red-600" />
              {/if}
              <span class="truncate text-[13.5px] font-semibold text-navy">{acct.email_address}</span>
            </div>
            <div class="mt-0.5 text-[11.5px] text-dark-gray">
              {acct.last_synced_at ? `Last synced: ${formatIST(acct.last_synced_at)}` : 'Never synced yet.'}
              {#if acct.status === 'error' && acct.last_error}
                &mdash; {acct.last_error}
              {/if}
            </div>
          </div>
          <button
            type="button"
            class="btn-secondary"
            onclick={() => syncMutation.mutate(acct.id)}
            disabled={syncMutation.isPending}
          >
            {#if syncMutation.isPending}<LoaderCircle class="h-4 w-4 animate-spin" />{/if}
            {syncMutation.isPending ? 'Syncing...' : 'Sync now'}
          </button>
        </div>
      {/each}
      <button type="button" class="btn-secondary" onclick={() => route.push({ tab: 'settings' })}>
        Manage in Settings
      </button>
    </div>

    {#if (trackedDomainsQuery.data?.results.length ?? 0) > 0}
      <div class="tym-card mb-5 px-4 py-3">
        <button
          type="button"
          class="flex w-full items-center gap-2 text-left"
          onclick={() => (trackedDomainsOpen = !trackedDomainsOpen)}
        >
          <Globe class="h-4 w-4 shrink-0 text-dark-gray" />
          <span class="flex-1 text-[13px] font-semibold text-navy">
            Domains tracked in your vertical ({trackedDomainsQuery.data?.results.length})
          </span>
          <ChevronDown class="h-4 w-4 shrink-0 text-dark-gray transition-transform {trackedDomainsOpen ? 'rotate-180' : ''}" />
        </button>
        {#if trackedDomainsOpen}
          <ul class="mt-2.5 space-y-1.5 border-t border-toyama-cloudy-gray pt-2.5">
            {#each trackedDomainsQuery.data?.results ?? [] as d (d.domain)}
              <li class="flex flex-wrap items-center gap-x-2 text-[12.5px]">
                <span class="font-medium text-navy">@{d.domain}</span>
                <span class="text-dark-gray">&mdash; {d.company_name ?? 'no company linked'}</span>
                <span class="text-dark-gray">&middot; {d.vertical_name}</span>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
    {/if}

    <EmailReviewQueue
      reviewApi={meEmailReviewApi}
      backfillApi={meEmailBackfillApi}
      queryKey={['email-review']}
      enabled={connected}
    />
    {/if}
  {/if}
</div>

{#if inboxDetailRow}
  <EmailActivityModal
    activity={inboxDetailRow}
    onClose={() => (inboxDetailRow = null)}
    onDismissed={() => {
      inboxDetailRow = null;
      queryClient.invalidateQueries({ queryKey: ['team-inbox'] });
    }}
  />
{/if}
